import React from 'react';
import { useArticleContext } from '../../ArticleContext';
import { isDisabledStageId } from '../../stageRegistry';
import type { PipelineStage } from './articleTypes';

function formatCost(cost: number | undefined): string {
  if (cost == null || cost === 0) return '';
  if (cost < 0.01) return '<$0.01';
  return `$${cost.toFixed(2)}`;
}

export function PipelineSidebar({ stages, activeStageId, onSelectStage }: {
  stages: PipelineStage[];
  activeStageId: string | null;
  onSelectStage: (stageId: string) => void;
}) {
  const { run } = useArticleContext();
  const runStages = run?.stages || [];

  const navy = '#1a2332';
  const navyLight = '#2d3748';
  const green = '#16a34a';
  const cyan = '#01bcd4';

  const completedCount = runStages.filter((s: { status: string }) => s.status === 'succeeded').length;

  return (
    <>
      {stages.map((stage) => {
        const isDisabled = isDisabledStageId(stage.id);
        const isSelected = activeStageId === stage.id;
        const stageRecord = runStages.find((s: { stage_id: string; status: string }) => s.stage_id === stage.id);
        const isCompleted = stageRecord?.status === 'succeeded';
        const isFailed = stageRecord?.status === 'failed';
        const isRunning = stageRecord?.status === 'running';
        const stageCost = (stageRecord as any)?.total_cost_usd
          ? Number((stageRecord as any).total_cost_usd)
          : undefined;
        const runCount = ((stageRecord as any)?.runs?.length) || 0;

        const pct = isCompleted ? 100 : stages.length > 0 ? Math.round((completedCount / stages.length) * 100) : 0;
        const stageName = (stage.name || stage.id).replace(/^Stage \d+[a-z]?:\s*/, '');
        const stageNumber = stage.id.replace('stage_', '').split('_')[0];

        const cardBorder = isSelected ? cyan : isCompleted ? green : isFailed ? '#ef4444' : navyLight;

        return (
          <div key={stage.id} className="relative pl-8 mb-3">
            <div className="absolute left-[11px] top-5 w-[10px] h-[10px] rounded-full z-10 border-2"
              style={{
                backgroundColor: isSelected ? cyan : isCompleted ? green : isRunning ? navy : isFailed ? '#ef4444' : navyLight,
                borderColor: '#f5f5f7',
              }}
            />
            <div
              className={`relative rounded-xl bg-white p-3 cursor-pointer transition-all duration-150 max-w-[260px]
                ${isSelected ? 'shadow-lg' : 'hover:shadow-md'}
                ${isDisabled ? 'cursor-not-allowed opacity-40' : ''}
              `}
              style={{ borderWidth: '2px', borderStyle: 'solid', borderColor: cardBorder }}
              onClick={() => { if (!isDisabled) onSelectStage(stage.id); }}
            >
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-mono font-bold uppercase"
                  style={{ color: isCompleted ? green : isRunning ? navy : '#94a3b8' }}
                >{stageNumber}</span>
                <span className="text-[13px] font-semibold leading-tight"
                  style={{ color: isSelected ? '#0e7490' : isCompleted ? '#374151' : isRunning ? navy : '#64748b' }}
                >{stageName}</span>
              </div>
              <div className="mt-1 text-[11px] flex items-center justify-between gap-2"
                style={{ color: isCompleted ? green : isFailed ? '#ef4444' : isRunning ? navy : '#9ca3af' }}
              >
                <span>{isCompleted ? 'Completed' : isFailed ? 'Failed' : isRunning ? 'Processing...' : isDisabled ? 'Disabled' : 'Pending'}</span>
                {stageCost !== undefined && stageCost > 0 && (
                  <span
                    className="text-[10px] font-mono text-gray-500"
                    title={runCount > 1 ? `${runCount} runs · total ${formatCost(stageCost)}` : formatCost(stageCost)}
                  >
                    {formatCost(stageCost)}{runCount > 1 ? ` ×${runCount}` : ''}
                  </span>
                )}
              </div>
              {isRunning ? (
                <div className="mt-2 h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: '#e5e7eb', marginRight: '50px' }}>
                  <div className="h-full w-1/3 rounded-full"
                    style={{
                      background: `linear-gradient(90deg, ${navy}, ${navyLight}, ${navy})`,
                      animation: 'shimmer 1.5s ease-in-out infinite',
                    }}
                  />
                </div>
              ) : (
                <div className="mt-1 text-[10px] font-mono text-gray-300">{stage.id}</div>
              )}
              {isRunning && (
                <div className="absolute bottom-2 right-2">
                  <svg className="w-9 h-9" viewBox="0 0 36 36">
                    <circle cx="18" cy="18" r="14" fill="none" stroke="#e5e7eb" strokeWidth="3" />
                    <circle cx="18" cy="18" r="14" fill="none" stroke={navy} strokeWidth="3"
                      strokeDasharray={`${pct * 0.88} ${88 - pct * 0.88}`}
                      strokeDashoffset="22" strokeLinecap="round"
                    />
                    <text x="18" y="18" textAnchor="middle" dominantBaseline="central"
                      className="text-[8px] font-bold" fill={navy}
                    >{pct}%</text>
                  </svg>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </>
  );
}
